// openDirections.js
// Shared helper: asks the user to confirm, then opens Google Maps directions
// to a given location. Works on the PWA (web) as well as the native iOS and
// Android builds (Capacitor) from the single shared React codebase.
import { Capacitor } from '@capacitor/core';

const MESSAGES = {
  TH: {
    confirm: 'เปิด Google Maps เพื่อนำทางไปยังสถานที่นี้?',
    noLocation: 'ไม่พบตำแหน่งของสถานที่นี้',
  },
  CN: {
    confirm: '打开 Google 地图导航到该位置？',
    noLocation: '找不到该地点的位置信息。',
  },
  EN: {
    confirm: 'Open Google Maps to navigate to this location?',
    noLocation: 'Location for this place is not available.',
  },
};

// Build a Google Maps directions URL. This universal link opens the native
// Google Maps app when installed (iOS/Android) and falls back to the web map.
const buildDirectionsUrl = (destination) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;

/**
 * Ask for confirmation, then open Google Maps navigation to the location.
 *
 * @param {Object}  opts
 * @param {number|string} [opts.latitude]   Destination latitude.
 * @param {number|string} [opts.longitude]  Destination longitude.
 * @param {string}        [opts.address]    Fallback destination when no coords.
 * @param {string}        [opts.language]   'TH' | 'CN' | 'EN' (default EN).
 * @returns {boolean} true if navigation was launched, false otherwise.
 */
export function openDirections({ latitude, longitude, address, language = 'EN' } = {}) {
  const t = MESSAGES[language] || MESSAGES.EN;

  const lat = Number(latitude);
  const lng = Number(longitude);
  const hasCoords =
    latitude !== null && latitude !== undefined && !Number.isNaN(lat) &&
    longitude !== null && longitude !== undefined && !Number.isNaN(lng);

  const destination = hasCoords
    ? `${lat},${lng}`
    : (address ? String(address).trim() : '');

  if (!destination) {
    // eslint-disable-next-line no-alert
    alert(t.noLocation);
    return false;
  }

  // Ask the user to confirm / grant permission before leaving the app.
  // window.confirm renders a native dialog inside the iOS & Android webviews.
  // eslint-disable-next-line no-alert
  if (!window.confirm(t.confirm)) return false;

  const url = buildDirectionsUrl(destination);

  try {
    if (Capacitor && typeof Capacitor.isNativePlatform === 'function' && Capacitor.isNativePlatform()) {
      // On native, "_system" hands the URL to the OS, which routes it to the
      // Google Maps app (or the default browser) for turn-by-turn navigation.
      window.open(url, '_system');
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } catch (e) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  return true;
}

export default openDirections;
